import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import CredentialStatusCard from '@/components/CredentialStatusCard'
import { CloverMappingsCard } from '@/components/ToolMappingSettings'
import JournalEntryPreview, { type JournalEntryLineBadge } from '@/components/JournalEntryPreview'
import WarningBanner from '@/components/WarningBanner'
import CloverGiftCardsCard from '@/components/CloverGiftCardsCard'
import CloverReconciliationStrip, {
  type CloverReconciliation,
} from '@/components/CloverReconciliationStrip'
import AccountingToolPage from '@/components/AccountingToolPage'
import { toast } from '@/store/toastStore'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { getSettings } from '@/services/settings'
import {
  ALL_STORES_ID,
  createCloverDraft,
  fetchCloverDateStatuses,
  refreshCloverDateStatuses,
  fetchCloverReports,
  pushCloverDraftQboUpdate,
  type CloverDraftResponse,
  type CloverFetchResponse,
  type CloverJournalEntryDateCount,
  type CloverSalesReport,
} from '@/services/cloverReports'
import {
  getQboIdMappings,
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
  type QboIdMappingItem,
} from '@/services/accounting'
import { formatApiError } from '@/utils/apiError'
import { isCloverAuthError } from '@/services/cloverExtension'
import {
  DEFAULT_CLOVER_STORE_OPTIONS,
  useCloverReportsStore,
  type CloverStoreResult,
} from '@/store/cloverReportsStore'
import { normalizeCloverJournalLineLocations } from '@/pages/clover/cloverJournalEntryLines'
import { getWaterfallSettings } from '@/services/waterfall'
import { getRetailObservances } from '@/utils/retailObservances'
import { todayIsoDate } from '@/utils/waterfallCalculator'
import { formatJePostedDate } from '@/pages/distribution-waterfall/waterfallJeStatus'
import { usePageAccess } from '@/hooks/usePageAccess'
import GiftCardsReconcile from '@/pages/gift-cards/GiftCardsReconcile'
import CloverBackfillAudit from '@/pages/clover/CloverBackfillAudit'
import { useCloverJobSession } from '@/pages/clover/useCloverJobSession'
import { useBackgroundJobsStore } from '@/store/backgroundJobsStore'
import { useCloverNavStore } from '@/store/cloverNavStore'
import { isBackgroundJobActive } from '@/services/jobs'
import {
  JournalEntryDocChip,
  JournalEntryExternalLink,
} from '@cottage/design-system/internal-tooling/ui'

/** True when reportDate is yesterday or later in America/Chicago (Clover lag). */
function isReportDateTooRecent(dateStr: string): boolean {
  if (!dateStr) return false
  const chicagoToday = todayIsoDate()
  const parts = chicagoToday.split('-').map((part) => Number(part))
  const ty = parts[0] ?? 1970
  const tm = parts[1] ?? 1
  const td = parts[2] ?? 1
  const maxAllowed = new Date(ty, tm - 1, td)
  maxAllowed.setDate(maxAllowed.getDate() - 2)
  const maxAllowedIso = [
    maxAllowed.getFullYear(),
    String(maxAllowed.getMonth() + 1).padStart(2, '0'),
    String(maxAllowed.getDate()).padStart(2, '0'),
  ].join('-')
  return dateStr > maxAllowedIso
}

function shiftIsoDate(dateStr: string, deltaDays: number): string {
  const base = dateStr ? new Date(`${dateStr}T12:00:00`) : new Date()
  base.setDate(base.getDate() + deltaDays)
  return base.toISOString().slice(0, 10)
}

function formatMoney(value: number | string): string {
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function tenderAmount(salesReport: CloverSalesReport, tenderName: string): number {
  const tender = salesReport.tender_types.find((t) => t.name === tenderName)
  return tender ? Number(tender.amount) : 0
}

function buildLineBadges(draft: CloverDraftResponse): Record<number, JournalEntryLineBadge> | undefined {
  if (!draft.line_meta) return undefined
  const badges: Record<number, JournalEntryLineBadge> = {}
  draft.line_meta.forEach((meta, index) => {
    if (meta.classification || meta.hint) {
      badges[index] = {
        label: [meta.classification, meta.hint].filter(Boolean).join(' · '),
        tone: meta.classification?.toLowerCase().includes('unclassified') ? 'amber' : 'gray',
      }
    }
  })
  return badges
}

function isLinesBalanced(lines: JournalEntryLine[]): boolean {
  const debits = lines
    .filter((line) => line.posting_type === 'Debit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  const credits = lines
    .filter((line) => line.posting_type === 'Credit')
    .reduce((sum, line) => sum + Number(line.amount), 0)
  return Math.abs(debits - credits) < 0.005
}

function cloverNeedsQboUpdate(draft: CloverDraftResponse): boolean {
  if (draft.needs_qbo_update) return true
  return draft.source_data?.needs_qbo_update === true
}

function departmentForStore(
  storeName: string,
  mappings: QboIdMappingItem[]
): Pick<JournalEntryLine, 'department_id' | 'department_name'> {
  const keyByStore: Record<string, string> = {
    'River Oaks': 'department.river_oaks',
    'Highland Park': 'department.highland_park',
    'G&A': 'department.g_and_a',
  }
  const key = keyByStore[storeName]
  if (!key) {
    return { department_id: null, department_name: null }
  }
  const mapping = mappings.find((item) => item.key === key)
  return mapping
    ? { department_id: mapping.qbo_id, department_name: mapping.display_name }
    : { department_id: null, department_name: null }
}

function ReportSummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 gap-y-1 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="text-right text-gray-900">{value}</span>
    </div>
  )
}

function CloverJeHistoryDrawer({
  open,
  onOpen,
  onClose,
  loading,
  error,
  rows,
  holidaysByDate,
  onSelectDate,
  onRefresh,
}: {
  open: boolean
  onOpen: () => void
  onClose: () => void
  loading: boolean
  error: string | null
  rows: CloverJournalEntryDateCount[]
  holidaysByDate: Record<string, string>
  onSelectDate: (date: string) => void
  onRefresh: () => void
}) {
  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className={`fixed right-0 top-1/3 z-30 rounded-l-lg border border-r-0 border-gray-200 bg-white px-2 py-3 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50 ${
          open ? 'hidden' : ''
        }`}
        style={{ writingMode: 'vertical-rl' }}
      >
        JE history
      </button>

      {open ? (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/10"
            onClick={onClose}
            aria-hidden="true"
          />
          <div className="fixed right-0 top-0 z-50 flex h-full w-fit max-w-[85vw] flex-col overflow-y-auto border-l border-gray-200 bg-white p-4 shadow-xl">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Clover JE history</h3>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onRefresh}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Refresh"
                  disabled={loading}
                >
                  <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Last 60 days through yesterday, America/Chicago
            </p>

            <div className="mt-3 flex justify-center">
              {loading ? (
                <p className="text-sm text-gray-500">Loading history…</p>
              ) : error ? (
                <p className="text-sm text-red-600">{error}</p>
              ) : (
                <div className="inline-block overflow-hidden rounded-lg border border-gray-200">
                  <table className="table-fixed border-separate border-spacing-0 text-xs">
                    <colgroup>
                      <col className="w-[125px]" />
                      <col className="w-[125px]" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th className="border-b border-gray-200 bg-gray-50 px-1.5 py-1.5 text-left font-medium text-gray-600">
                          Date
                        </th>
                        <th className="border-b border-gray-200 bg-gray-50 px-1.5 py-1.5 text-right font-medium text-gray-600">
                          JE Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const holidayName = holidaysByDate[row.date]
                        return (
                          <tr
                            key={row.date}
                            onClick={() => {
                              onSelectDate(row.date)
                              onClose()
                            }}
                            className={`cursor-pointer hover:bg-blue-50 ${
                              row.complete === false && row.actionable !== false
                                ? 'bg-amber-50'
                                : ''
                            }`}
                          >
                            <td className="border-b border-gray-100 px-1.5 py-1.5 text-gray-900">
                              <div>{row.date}</div>
                              {holidayName ? (
                                <div className="italic text-gray-400" style={{ fontSize: '9px' }}>
                                  {holidayName}
                                </div>
                              ) : null}
                            </td>
                            <td className="border-b border-gray-100 px-1.5 py-1.5 text-right text-gray-900">
                              {row.je_count === 0 ? '–' : row.je_count}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </>
  )
}

type TabId = 'allocate' | 'settings' | 'giftcards' | 'audit'

function tabFromParam(value: string | null): TabId {
  if (
    value === 'settings' ||
    value === 'giftcards' ||
    value === 'allocate' ||
    value === 'audit'
  ) {
    return value
  }
  return 'allocate'
}

export default function CloverDailyReports() {
  const store = useCloverReportsStore((state) => state.store)
  const reportDate = useCloverReportsStore((state) => state.reportDate)
  const results = useCloverReportsStore((state) => state.results)
  const showAuthRecovery = useCloverReportsStore((state) => state.showAuthRecovery)
  const setStore = useCloverReportsStore((state) => state.setStore)
  const setReportDate = useCloverReportsStore((state) => state.setReportDate)
  const setResults = useCloverReportsStore((state) => state.setResults)
  const updateResult = useCloverReportsStore((state) => state.updateResult)
  const setShowAuthRecovery = useCloverReportsStore((state) => state.setShowAuthRecovery)
  const expireIfStale = useCloverReportsStore((state) => state.expireIfStale)
  const { canEdit } = usePageAccess('clover-reports')
  const [searchParams, setSearchParams] = useSearchParams()

  const [tab, setTab] = useState<TabId>(() => tabFromParam(searchParams.get('tab')))

  useEffect(() => {
    const next = tabFromParam(searchParams.get('tab'))
    setTab((current) => (current === next ? current : next))
  }, [searchParams])

  const selectTab = (next: TabId) => {
    setTab(next)
    const params = new URLSearchParams(searchParams)
    if (next === 'allocate') {
      params.delete('tab')
    } else {
      params.set('tab', next)
    }
    setSearchParams(params, { replace: true })
  }

  const [storeOptions, setStoreOptions] = useState<SelectOption[]>(DEFAULT_CLOVER_STORE_OPTIONS)
  const [accountOptions, setAccountOptions] = useState<SelectOption[]>([])
  const [qboMappings, setQboMappings] = useState<QboIdMappingItem[]>([])
  const [fetching, setFetching] = useState(false)
  const [postingDraftId, setPostingDraftId] = useState<number | null>(null)
  const [savingDraftId, setSavingDraftId] = useState<number | null>(null)
  const [draftActionId, setDraftActionId] = useState<number | null>(null)
  const [postedJeMenuOpenIds, setPostedJeMenuOpenIds] = useState<Set<number>>(new Set())
  const postedJeMenuRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [historyOpen, setHistoryOpen] = useState(false)
  const [history, setHistory] = useState<CloverJournalEntryDateCount[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const [holidaysByDate, setHolidaysByDate] = useState<Record<string, string>>({})
  const [collapsedDraftIds, setCollapsedDraftIds] = useState<Set<number>>(new Set())
  const [refreshingGiftCardDraftId, setRefreshingGiftCardDraftId] = useState<number | null>(null)
  const [rebuildingGiftCardDraftId, setRebuildingGiftCardDraftId] = useState<number | null>(null)
  const [giftCardRefreshPending, setGiftCardRefreshPending] = useState<Set<number>>(new Set())
  const fetchGenerationRef = useRef(0)
  const fetchCloverNavStatus = useCloverNavStore((s) => s.fetchNavStatus)
  const allocateIncompleteCount = useCloverNavStore((s) => s.allocateIncompleteCount)
  const giftCardIncompleteCount = useCloverNavStore((s) => s.giftCardIncompleteCount)

  const serverById = useBackgroundJobsStore((s) => s.serverById)
  const activeBackfillJob = useMemo(() => {
    for (const { job } of Object.values(serverById)) {
      if (
        job.job_type === 'clover_backfill_audit' &&
        isBackgroundJobActive(job.status)
      ) {
        return job
      }
    }
    return null
  }, [serverById])
  const cloverBackfillJobStatus = activeBackfillJob?.status ?? null
  const cloverJobSession = useCloverJobSession(cloverBackfillJobStatus)

  const configuredStores = useMemo(
    () => storeOptions.filter((opt) => opt.id !== ALL_STORES_ID).map((opt) => opt.id),
    [storeOptions]
  )
  const locationOptions = useMemo(
    () =>
      qboMappings
        .filter((mapping) => mapping.mapping_type === 'department')
        .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [qboMappings]
  )

  const journalLinesByDraftId = useMemo(() => {
    const byId = new Map<number, JournalEntryLine[]>()
    for (const result of results) {
      const storeDepartment = departmentForStore(result.storeName, qboMappings)
      byId.set(
        result.draft.id,
        normalizeCloverJournalLineLocations(result.draft.lines, storeDepartment),
      )
    }
    return byId
  }, [results, qboMappings])

  useEffect(() => {
    if (showAuthRecovery) {
      selectTab('settings')
    }
  }, [showAuthRecovery])

  useEffect(() => {
    expireIfStale()
    void loadStoreOptions()
    void loadQboMappings()
    void loadStoredDateStatuses()
    void refreshDateStatusesInBackground()
    void loadFederalHolidays()
  }, [expireIfStale])

  useEffect(() => {
    if (postedJeMenuOpenIds.size === 0) return
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      for (const draftId of postedJeMenuOpenIds) {
        const menuEl = postedJeMenuRefs.current.get(draftId)
        if (menuEl?.contains(target)) return
      }
      setPostedJeMenuOpenIds(new Set())
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setPostedJeMenuOpenIds(new Set())
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [postedJeMenuOpenIds])

  async function loadFederalHolidays() {
    const currentYear = new Date().getFullYear()
    const byDate: Record<string, string> = getRetailObservances(currentYear - 1, currentYear + 1)
    try {
      const settings = await getWaterfallSettings()
      const item = settings.items.find((entry) => entry.key === 'federal_holidays')
      const rows = (item?.value as { date: string; name: string }[] | undefined) ?? []
      for (const row of rows) {
        if (row?.date && row?.name) byDate[row.date] = row.name
      }
    } catch (err) {
      console.error('Failed to load federal holidays:', err)
    }
    setHolidaysByDate(byDate)
  }

  async function loadStoredDateStatuses() {
    try {
      setHistoryLoading(true)
      setHistoryError(null)
      setHistory(await fetchCloverDateStatuses())
    } catch (err) {
      setHistoryError(formatApiError(err, 'Unable to load Clover JE history'))
    } finally {
      setHistoryLoading(false)
    }
  }

  async function refreshDateStatusesInBackground() {
    try {
      setHistory(await refreshCloverDateStatuses())
      await fetchCloverNavStatus()
    } catch (err) {
      console.error('Clover date-status refresh failed:', err)
    }
  }

  async function loadJournalEntryHistory() {
    try {
      setHistoryLoading(true)
      setHistoryError(null)
      setHistory(await refreshCloverDateStatuses())
      await fetchCloverNavStatus()
    } catch (err) {
      setHistoryError(formatApiError(err, 'Unable to refresh Clover JE history'))
    } finally {
      setHistoryLoading(false)
    }
  }

  async function loadQboMappings() {
    try {
      const response = await getQboIdMappings()
      setQboMappings(response.mappings)
      setAccountOptions(
        response.mappings
          .filter((mapping) => mapping.mapping_type === 'account')
          .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
          .sort((a, b) => a.label.localeCompare(b.label))
      )
    } catch (err) {
      console.error('Failed to load QBO account mappings:', err)
    }
  }

  async function loadStoreOptions() {
    try {
      const settings = await getSettings()
      const raw = settings.settings.clover_store_merchant_ids?.value ?? '{}'
      const parsed = JSON.parse(raw) as Record<string, string>
      const storeNames = Object.keys(parsed).sort()
      const names =
        storeNames.length > 0 ? storeNames : ['Highland Park', 'River Oaks']
      setStoreOptions([
        { id: ALL_STORES_ID, label: 'All Stores' },
        ...names.map((name) => ({ id: name, label: name })),
      ])
    } catch (err) {
      console.error('Failed to load Clover store list:', err)
      setStoreOptions(DEFAULT_CLOVER_STORE_OPTIONS)
    }
  }

  function storesToProcess(): string[] {
    if (!store) return []
    if (store === ALL_STORES_ID) {
      return configuredStores
    }
    return [store]
  }

  function seedGiftCardClassifications(
    draft: CloverDraftResponse,
    existing?: Record<string, string>,
  ): Record<string, string> {
    return {
      ...(draft.gift_card_recon?.classifications ?? {}),
      ...existing,
    }
  }

  async function rebuildCloverDraftFromResult(
    result: CloverStoreResult,
    options: {
      fetchData?: CloverFetchResponse
      gift_card_classifications?: Record<string, string>
    } = {},
  ): Promise<CloverStoreResult> {
    const fetchData = options.fetchData ?? result.fetchData
    const gift_card_classifications =
      options.gift_card_classifications ?? result.gift_card_classifications
    const draft = await createCloverDraft({
      store_name: result.storeName,
      report_date: reportDate,
      sales_report: fetchData.sales_report,
      deposit_report: fetchData.deposit_report,
      gift_card_classifications,
      gift_card_activity: fetchData.gift_card_activity ?? null,
    })
    return {
      storeName: result.storeName,
      fetchData,
      draft,
      gift_card_classifications: seedGiftCardClassifications(draft, gift_card_classifications),
    }
  }

  async function handleFetchAndDraft() {
    const targetStores = storesToProcess()
    if (targetStores.length === 0 || !reportDate) {
      return
    }
    if (isReportDateTooRecent(reportDate)) {
      clearDraftResults()
      return
    }

    const generation = ++fetchGenerationRef.current
    const priorClassificationsByStore = new Map(
      results
        .filter((result) => result.fetchData.sales_report.report_date === reportDate)
        .map((result) => [result.storeName, result.gift_card_classifications]),
    )

    try {
      setFetching(true)
      setShowAuthRecovery(false)
      setResults([])
      setCollapsedDraftIds(new Set())
      setPostedJeMenuOpenIds(new Set())
      setGiftCardRefreshPending(new Set())

      const nextResults: CloverStoreResult[] = []
      const failures: string[] = []

      for (const storeName of targetStores) {
        if (generation !== fetchGenerationRef.current) return
        try {
          const fetched = await fetchCloverReports({
            store_name: storeName,
            report_date: reportDate,
          })
          const gift_card_classifications = priorClassificationsByStore.get(storeName) ?? {}
          const draftResult = await createCloverDraft({
            store_name: storeName,
            report_date: reportDate,
            sales_report: fetched.sales_report,
            deposit_report: fetched.deposit_report,
            gift_card_activity: fetched.gift_card_activity ?? null,
            gift_card_classifications,
          })
          nextResults.push({
            storeName,
            fetchData: fetched,
            draft: draftResult,
            gift_card_classifications: seedGiftCardClassifications(
              draftResult,
              gift_card_classifications,
            ),
          })
        } catch (err) {
          if (isCloverAuthError(err)) {
            setShowAuthRecovery(true)
          }
          failures.push(`${storeName}: ${formatApiError(err, 'Failed to fetch Clover reports')}`)
        }
      }

      if (generation !== fetchGenerationRef.current) return

      setResults(nextResults)

      for (const result of nextResults) {
        if (result.draft.status === 'posted') {
          if (cloverNeedsQboUpdate(result.draft)) {
            toast.success(
              `${result.storeName} already posted — use Update Existing JE to push changes`
            )
          } else {
            toast.success(`${result.storeName} is already posted to QuickBooks`)
          }
        }
      }

      if (failures.length > 0) {
        toast.error(failures.join('\n'))
      } else if (nextResults.length === 0) {
        toast.error('No drafts were created')
      }
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return
      if (isCloverAuthError(err)) {
        setShowAuthRecovery(true)
      }
      toast.error(formatApiError(err, 'Failed to fetch Clover reports'))
    } finally {
      if (generation === fetchGenerationRef.current) {
        setFetching(false)
      }
    }
  }

  useEffect(() => {
    if (!reportDate) return
    if (isReportDateTooRecent(reportDate)) {
      clearDraftResults()
      return
    }
    if (!store) return
    void handleFetchAndDraft()
    // Auto-fetch when report date or store changes (fetch button removed).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional trigger on date/store only
  }, [reportDate, store])

  useEffect(() => {
    if (!reportDate || !isReportDateTooRecent(reportDate)) return
    toast.error("Reports aren't available for that date yet.")
  }, [reportDate])

  function toggleDraftCollapsed(draftId: number) {
    setCollapsedDraftIds((prev) => {
      const next = new Set(prev)
      if (next.has(draftId)) {
        next.delete(draftId)
      } else {
        next.add(draftId)
      }
      return next
    })
  }

  function clearDraftResults() {
    fetchGenerationRef.current += 1
    setFetching(false)
    setResults([])
    setCollapsedDraftIds(new Set())
    setPostingDraftId(null)
    setSavingDraftId(null)
    setDraftActionId(null)
    setPostedJeMenuOpenIds(new Set())
    setGiftCardRefreshPending(new Set())
  }

  async function handleGiftCardClassificationChange(
    draftId: number,
    overrideKey: string,
    classification: string,
  ) {
    const existing = results.find((result) => result.draft.id === draftId)
    if (!existing) return

    const gift_card_classifications = {
      ...existing.gift_card_classifications,
      [overrideKey]: classification,
    }

    updateResult(draftId, { gift_card_classifications })

    try {
      setRebuildingGiftCardDraftId(draftId)
      const rebuilt = await rebuildCloverDraftFromResult(existing, {
        gift_card_classifications,
      })
      updateResult(draftId, rebuilt)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save gift card classification'))
    } finally {
      setRebuildingGiftCardDraftId(null)
    }
  }

  async function handleGiftCardRefresh(draftId: number) {
    const existing = results.find((result) => result.draft.id === draftId)
    if (!existing) return

    try {
      setRefreshingGiftCardDraftId(draftId)
      const fetched = await fetchCloverReports(
        {
          store_name: existing.storeName,
          report_date: reportDate,
        },
        { refresh_gift_cards: true },
      )
      const rebuilt = await rebuildCloverDraftFromResult(existing, {
        fetchData: fetched,
      })
      updateResult(draftId, rebuilt)
      setGiftCardRefreshPending((prev) => {
        const next = new Set(prev)
        next.add(draftId)
        return next
      })
      toast.success(`Refreshed gift card processor report for ${existing.storeName}`)
    } catch (err) {
      if (isCloverAuthError(err)) {
        setShowAuthRecovery(true)
      }
      toast.error(formatApiError(err, 'Failed to refresh gift card processor report'))
    } finally {
      setRefreshingGiftCardDraftId(null)
    }
  }

  function handleReportDateChange(nextDate: string) {
    setReportDate(nextDate)
  }

  function handleShiftReportDate(deltaDays: number) {
    setReportDate(shiftIsoDate(reportDate, deltaDays))
  }

  function handleRetryAfterTokenSave() {
    if (store && reportDate && !isReportDateTooRecent(reportDate)) {
      void handleFetchAndDraft()
    }
  }

  async function handleLinesChange(draftId: number, nextLines: JournalEntryLine[]) {
    const existing = results.find((result) => result.draft.id === draftId)
    if (!existing) return

    updateResult(draftId, {
      draft: {
        ...existing.draft,
        lines: nextLines,
      },
    })

    try {
      setSavingDraftId(draftId)
      const updated = await updateJournalEntryLines(draftId, nextLines)
      updateResult(draftId, {
        draft: {
          ...existing.draft,
          lines: updated.lines,
          total_debits: updated.total_debits,
          total_credits: updated.total_credits,
          warnings: updated.warnings ?? existing.draft.warnings,
        },
      })
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save line changes'))
    } finally {
      setSavingDraftId(null)
    }
  }

  async function handlePost(draftId: number) {
    const existing = results.find((result) => result.draft.id === draftId)
    if (!existing) return

    const storeDepartment = departmentForStore(existing.storeName, qboMappings)
    const journalLines = normalizeCloverJournalLineLocations(
      existing.draft.lines,
      storeDepartment,
    )

    try {
      setPostingDraftId(draftId)
      if (JSON.stringify(journalLines) !== JSON.stringify(existing.draft.lines)) {
        const updated = await updateJournalEntryLines(draftId, journalLines)
        updateResult(draftId, {
          draft: {
            ...existing.draft,
            lines: updated.lines,
            total_debits: updated.total_debits,
            total_credits: updated.total_credits,
            warnings: updated.warnings ?? existing.draft.warnings,
          },
        })
      }
      const response = await postJournalEntry(draftId)
      updateResult(draftId, {
        draft: {
          ...existing.draft,
          status: response.draft.status,
          qbo_url: response.draft.qbo_url,
          qbo_entity_id: response.draft.qbo_entity_id,
        },
      })
      toast.success(`Posted ${response.draft.doc_number} to QuickBooks`)
      void refreshDateStatusesInBackground()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to post journal entry'))
    } finally {
      setPostingDraftId(null)
    }
  }

  function togglePostedJeMenu(draftId: number) {
    setPostedJeMenuOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(draftId)) {
        next.delete(draftId)
      } else {
        next.add(draftId)
      }
      return next
    })
  }

  async function handlePushQboUpdate(draftId: number) {
    const existing = results.find((result) => result.draft.id === draftId)
    const confirmed = window.confirm(
      existing?.draft.doc_number
        ? `Rebuild ${existing.draft.doc_number} from the current Clover reports and overwrite the existing journal entry in QuickBooks?`
        : 'Rebuild this journal entry from the current Clover reports and overwrite the existing entry in QuickBooks?'
    )
    if (!confirmed) return

    setPostedJeMenuOpenIds((prev) => {
      const next = new Set(prev)
      next.delete(draftId)
      return next
    })
    setDraftActionId(draftId)
    try {
      const result = await pushCloverDraftQboUpdate(draftId, {
        gift_card_classifications: existing?.gift_card_classifications ?? {},
        refresh_gift_cards: giftCardRefreshPending.has(draftId),
        force_regenerate: true,
      })
      updateResult(draftId, {
        draft: result.draft,
        gift_card_classifications: seedGiftCardClassifications(
          result.draft,
          existing?.gift_card_classifications,
        ),
      })
      setGiftCardRefreshPending((prev) => {
        const next = new Set(prev)
        next.delete(draftId)
        return next
      })
      toast.success(
        result.regenerated
          ? 'Regenerated draft and updated QuickBooks'
          : 'Updated journal entry in QuickBooks'
      )
      void refreshDateStatusesInBackground()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update journal entry in QuickBooks'))
    } finally {
      setDraftActionId(null)
    }
  }

  const allocateTabAlertLabel =
    allocateIncompleteCount === 1
      ? '1 Clover date missing QuickBooks entries'
      : `${allocateIncompleteCount} Clover dates missing QuickBooks entries`
  const giftCardsTabAlertLabel =
    giftCardIncompleteCount === 1
      ? '1 gift card month missing the correct journal entry'
      : `${giftCardIncompleteCount} gift card months missing the correct journal entry`

  return (
    <AccountingToolPage
      title="Clover"
      description="Post daily Clover sales and deposit journal entries, and reconcile monthly gift card outstanding balances."
      headerExtra={
        <div className="overflow-x-auto">
          <div
            className="inline-flex min-w-max gap-2 rounded-lg bg-gray-200 p-1"
            role="tablist"
            aria-label="Clover reports sections"
          >
            {(
              [
                ['allocate', 'Allocate'],
                ['giftcards', 'Gift cards'],
                ['audit', 'Audit'],
                ['settings', 'Settings'],
              ] as const
            ).map(([id, label]) => {
              const showAllocateDot = id === 'allocate' && allocateIncompleteCount > 0
              const showGiftCardsDot = id === 'giftcards' && giftCardIncompleteCount > 0
              const dotLabel =
                id === 'allocate'
                  ? allocateTabAlertLabel
                  : id === 'giftcards'
                    ? giftCardsTabAlertLabel
                    : ''
              return (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => selectTab(id)}
                className={`relative flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
                  tab === id
                    ? 'bg-white text-brand-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
                {showAllocateDot || showGiftCardsDot ? (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-red-500"
                    aria-label={dotLabel}
                    title={dotLabel}
                  />
                ) : null}
              </button>
            )})}
          </div>
        </div>
      }
    >
      {tab === 'settings' ? (
        <>
          <CredentialStatusCard
            service="clover"
            showSettingsLink={false}
            authRecoveryHint={showAuthRecovery}
            onGrabSuccess={handleRetryAfterTokenSave}
            onCredentialsSaved={() => setShowAuthRecovery(false)}
            jobStatus={cloverBackfillJobStatus}
            cloverExtensionInstalled={cloverJobSession.extensionInstalled}
            cloverKeepaliveStatus={cloverJobSession.keepaliveStatus}
            cloverKeepaliveBusy={cloverJobSession.keepaliveBusy}
            onCloverKeepaliveToggle={(enabled) => void cloverJobSession.handleKeepaliveToggle(enabled)}
            onCloverTokensPushed={cloverJobSession.markTokensPushed}
          />
          <CloverMappingsCard onStoresSaved={() => void loadStoreOptions()} />
        </>
      ) : tab === 'giftcards' ? (
        <GiftCardsReconcile canEdit={canEdit} />
      ) : tab === 'audit' ? (
        <CloverBackfillAudit canEdit={canEdit} />
      ) : (
        <>
      <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="container-title">Report inputs</h2>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem]">
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Store</label>
            <SearchableSelect
              options={storeOptions}
              value={store}
              onChange={(id) => setStore(id)}
              placeholder="Select store…"
              onCreateNew={undefined}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">
              Report date
              {fetching ? (
                <span className="ml-2 font-normal text-gray-400">Fetching…</span>
              ) : null}
            </label>
            <div className="flex items-stretch gap-1">
              <button
                type="button"
                className="btn btn-secondary border border-gray-200 px-2 !min-h-0"
                onClick={() => handleShiftReportDate(-1)}
                aria-label="Previous day"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <DateInput
                value={reportDate}
                onChange={handleReportDateChange}
                dateFormat="iso"
                className="input text-sm"
              />
              <button
                type="button"
                className="btn btn-secondary border border-gray-200 px-2 !min-h-0"
                onClick={() => handleShiftReportDate(1)}
                aria-label="Next day"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <CloverJeHistoryDrawer
        open={historyOpen}
        onOpen={() => setHistoryOpen(true)}
        onClose={() => setHistoryOpen(false)}
        loading={historyLoading}
        error={historyError}
        rows={history}
        holidaysByDate={holidaysByDate}
        onSelectDate={handleReportDateChange}
        onRefresh={() => void loadJournalEntryHistory()}
      />

      {results.map(({ storeName, fetchData, draft, gift_card_classifications }) => {
        const balanced = isLinesBalanced(draft.lines)
        const showLineBadges =
          draft.line_meta != null && draft.lines.length === draft.line_meta.length
        const lineBadges = showLineBadges ? buildLineBadges(draft) : undefined
        const sales = fetchData.sales_report
        const deposit = fetchData.deposit_report
        const reconciliation = draft.source_data?.reconciliation as
          | CloverReconciliation
          | undefined
        const storeDepartment = departmentForStore(storeName, qboMappings)
        const journalLines = journalLinesByDraftId.get(draft.id) ?? draft.lines
        const isPosted = draft.status === 'posted'
        const needsQboUpdate = cloverNeedsQboUpdate(draft)
        const postedJeMenuOpen = postedJeMenuOpenIds.has(draft.id)
        const isCollapsed = collapsedDraftIds.has(draft.id)
        const txnDateLabel = formatJePostedDate(draft.txn_date)

        return (
          <section
            key={`${storeName}-${draft.id}`}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <div
              role="button"
              tabIndex={0}
              onClick={() => toggleDraftCollapsed(draft.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggleDraftCollapsed(draft.id)
                }
              }}
              className={`flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 cursor-pointer select-none ${
                isPosted
                  ? 'border-emerald-200 bg-emerald-50/70 hover:bg-emerald-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <ChevronDownIcon
                  className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                    isCollapsed ? '-rotate-90' : ''
                  }`}
                />
                <div>
                  <h2 className="je-card__title container-title">{storeName}</h2>
                  <p className="je-card__meta">
                    <JournalEntryDocChip>{draft.doc_number}</JournalEntryDocChip>
                    {txnDateLabel ? <span>{txnDateLabel}</span> : null}
                    {isPosted && draft.qbo_url ? (
                      <span onClick={(e) => e.stopPropagation()}>
                        <JournalEntryExternalLink href={draft.qbo_url}>
                          View in QuickBooks
                        </JournalEntryExternalLink>
                      </span>
                    ) : null}
                  </p>
                </div>
              </div>
              {isPosted ? (
                <div
                  className="relative inline-flex"
                  ref={(el) => {
                    if (el) postedJeMenuRefs.current.set(draft.id, el)
                    else postedJeMenuRefs.current.delete(draft.id)
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <div
                    className={`inline-flex h-9 items-stretch overflow-hidden rounded-md text-sm font-semibold ring-1 ${
                      needsQboUpdate
                        ? 'bg-amber-50 text-amber-950 ring-amber-300'
                        : 'bg-emerald-100 text-emerald-800 ring-emerald-200'
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5 px-3">
                      <CheckCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {draftActionId === draft.id ? 'Updating…' : 'Posted to QuickBooks'}
                    </span>
                    <button
                      type="button"
                      className={`inline-flex items-center border-l px-2 transition-colors disabled:opacity-60 ${
                        needsQboUpdate
                          ? 'border-amber-200 hover:bg-amber-100'
                          : 'border-emerald-200 hover:bg-emerald-200/70'
                      }`}
                      aria-label="Posted journal entry actions"
                      aria-haspopup="menu"
                      aria-expanded={postedJeMenuOpen}
                      disabled={draftActionId === draft.id}
                      onClick={() => togglePostedJeMenu(draft.id)}
                    >
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform ${
                          postedJeMenuOpen ? 'rotate-180' : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  {postedJeMenuOpen ? (
                    <div
                      role="menu"
                      className="absolute right-0 top-full z-20 mt-1 min-w-[12rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
                        onClick={() => void handlePushQboUpdate(draft.id)}
                      >
                        Update Existing JE
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary text-sm"
                  onClick={(e) => {
                    e.stopPropagation()
                    void handlePost(draft.id)
                  }}
                  disabled={
                    postingDraftId === draft.id ||
                    savingDraftId === draft.id ||
                    !balanced
                  }
                >
                  {postingDraftId === draft.id ? 'Posting…' : 'Post to QuickBooks'}
                </button>
              )}
            </div>

            {isCollapsed ? null : (
            <div className="space-y-5 p-5">
              {draft.warnings.length > 0 ? (
                <WarningBanner messages={draft.warnings} />
              ) : null}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-lg bg-gray-50/90 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">Sales report</h3>
                  <div className="mt-3 space-y-2">
                    <ReportSummaryRow label="Net sales" value={formatMoney(sales.net_sales)} />
                    <ReportSummaryRow
                      label="Tax collected"
                      value={formatMoney(sales.taxes_collected)}
                    />
                    <ReportSummaryRow label="Tips" value={formatMoney(sales.tips)} />
                  </div>
                  {sales.tender_types.length > 0 ? (
                    <div className="mt-4 space-y-2 border-t border-gray-200/80 pt-3">
                      {sales.tender_types.map((t) => (
                        <ReportSummaryRow key={t.name} label={t.name} value={formatMoney(t.amount)} />
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="rounded-lg bg-gray-50/90 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">Deposit report</h3>
                  <div className="mt-3 space-y-2">
                    <ReportSummaryRow
                      label="Amount transferred"
                      value={formatMoney(deposit.amount_transferred)}
                    />
                    <ReportSummaryRow
                      label="Amount submitted"
                      value={formatMoney(deposit.amount_submitted)}
                    />
                    <ReportSummaryRow label="Cash" value={formatMoney(tenderAmount(sales, 'Cash'))} />
                    <ReportSummaryRow label="Check" value={formatMoney(tenderAmount(sales, 'Check'))} />
                  </div>
                </div>
              </div>

              <CloverGiftCardsCard
                activity={fetchData.gift_card_activity}
                recon={draft.gift_card_recon}
                salesReport={sales}
                classifications={gift_card_classifications}
                canEdit={canEdit}
                refreshing={
                  refreshingGiftCardDraftId === draft.id ||
                  rebuildingGiftCardDraftId === draft.id
                }
                onClassificationChange={(overrideKey, classification) =>
                  void handleGiftCardClassificationChange(draft.id, overrideKey, classification)
                }
                onRefresh={() => void handleGiftCardRefresh(draft.id)}
                formatMoney={formatMoney}
              />

              <CloverReconciliationStrip reconciliation={reconciliation} lines={draft.lines} />

              <div>
                <h3 className="text-sm font-semibold text-gray-900">Draft journal entry</h3>
                <div className="mt-4">
                  <JournalEntryPreview
                    lines={journalLines}
                    variant="split"
                    hideHeader
                    editableAmounts={!isPosted}
                    editableLines={!isPosted}
                    editableLocations={!isPosted}
                    accountOptions={accountOptions}
                    locationOptions={locationOptions}
                    newLineDefaults={storeDepartment}
                    onLinesChange={
                      !isPosted ? (next) => void handleLinesChange(draft.id, next) : undefined
                    }
                    lineBadges={lineBadges}
                  />
                  {savingDraftId === draft.id ? (
                    <p className="mt-2 text-xs text-gray-500">Saving line changes…</p>
                  ) : null}
                </div>
              </div>
            </div>
            )}
          </section>
        )
      })}
        </>
      )}
    </AccountingToolPage>
  )
}
